import { Account, Counterparty, Entry, Transaction, Ref } from "../domain";
import { assertType, createGrouping } from "../base";

class RegisterEntry {
    private _entry: Entry;
    private _date: Date;
    private _counterparty: Ref<Counterparty>;
    
    constructor(entry:Entry, date:Date, counterparty:Ref<Counterparty>) {
        assertType(entry, "entry", Entry);
        assertType(date, "date", Date);
        assertType(counterparty, "counterparty", Ref);
        
        this._entry = entry;
        this._date = date;
        this._counterparty = counterparty;
    }

    get entry() { return this._entry;}
    get uuid() { return this._entry.uuid;}
    get account() { return this._entry.account; }
    get amount() { return this._entry.amount; }
    get date() { return this._date; }
    get counterparty() { return this._counterparty; }
}

class Register {
    private _account: Account;
    private _registerEntries: RegisterEntry[];
    
    constructor(account:Account, registerEntries:RegisterEntry[]) {
        assertType(account, "account", Account);
        assertType(registerEntries, "registerEntries", Array, RegisterEntry);
        this._account = account;
        this._registerEntries = registerEntries;
    }

    get account() { return this._account; }
    get registerEntries() { return this._registerEntries; }

    static registerEntries(transactions:Transaction[]) : RegisterEntry[] {
        const registerEntries = flatMap(transactions, tx => tx.entries.map((ent:Entry) => new RegisterEntry(ent, tx.date, tx.counterparty)));
        return registerEntries;
    }

    static from(transactions:Transaction[]) {

        const registerEntries = flatMap(transactions, tx => tx.entries.map((ent:Entry) => new RegisterEntry(ent, tx.date, tx.counterparty)));
        const registerEntriesGrouped = createGrouping(registerEntries, ent => ent.account.uuid);


        function addFlowsToParent(account:Account, registerEntries:RegisterEntry[], grouped:Record<string, RegisterEntry[]>) {
            const existingRegisterEntries = grouped[account.uuid] || [];
            grouped[account.uuid] = unique(existingRegisterEntries.concat(registerEntries));
            if (account.parent) {
                addFlowsToParent(account.parent.obj(), grouped[account.uuid], grouped);
            }
        }

        for (const [uuid, registerEntries] of Object.entries(registerEntriesGrouped)) {
            const account = Account.fromUuid(uuid);
            if (account && account.hasParent()) {
                addFlowsToParent(account, registerEntries, registerEntriesGrouped)
            }
        }

        const registers = Object.entries(registerEntriesGrouped)
            .map(([uuid, registerEntries]) => {
                const account = Account.fromUuid(uuid);
                if (!account) {
                    throw new Error("Illegal state");
                }
                return new Register(account, registerEntries);
            });

        return registers;
    }
}

function flatMap<T,V>(items:T[], mapper:(x:T) => V[]) : V[] {
    let flattened:V[] = [];
    items.forEach((item:T) => {
        const innerItems:V[] = mapper(item);
        flattened = flattened.concat(innerItems);
    });
    return flattened;
}

function unique<T>(items: T[]): T[] {
    const uniqueList:T[] = [];
    items.forEach(item => {
        if (uniqueList.indexOf(item) === -1) {
            uniqueList.push(item);
        }
    });
    return uniqueList;
}

export { RegisterEntry, Register };